import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import moment from "moment";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import { Select, TextInput } from "flowbite-react";
import { ModalShell } from "../../../atoms/ModalShell";
import { AiOutlineLoading } from "react-icons/ai";
import {
  HiOutlineCalendar,
  HiOutlineFilter,
  HiOutlineLockClosed,
  HiOutlineTrash,
  HiOutlineAdjustments,
  HiOutlinePlus,
  HiSwitchHorizontal,
  HiChevronDown,
} from "react-icons/hi";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useConvertLead,
  useCreateLead,
  useDeleteLead,
  useGetLeadColumns,
  useGetLeads,
  useGetLeadsSummary,
  useUpdateLead,
  leadsKey,
} from "../../../../hooks/leads";
import { Lead, UpdateLeadInput, leadLimit } from "../../../../types/LeadTypes";
import { CustomButton } from "../../../atoms/CustomButton";
import { ToolbarButton, ToolbarDivider } from "../../../atoms/ToolbarButton";
import { EmptyState } from "../../../atoms/EmptyState";
import NoTranslate from "../../../atoms/NoTranslate";
import UserAvatar from "../../../atoms/UserAvatar";
import AddLeadModal from "./AddLeadModal";
import LeadDetailModal from "./LeadDetailModal";
import ColumnsConfigModal from "./ColumnsConfigModal";
import LeadsGettingStarted from "./LeadsGettingStarted";
import CollapsibleHowItWorks from "../../../common/CollapsibleHowItWorks";
import ConfirmModal from "../../../public/ConfirmModal";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import {
  KanbanCard,
  KanbanColumn,
  KanbanDnd,
  useKanbanDndEnabled,
} from "../../../molecules/KanbanDnd";
import type { KanbanDndTexts } from "../../../molecules/KanbanDnd";
import { columnLabel, resolveColumnId } from "./leadColumns";

const apiErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const element = data?.element;
  if (element && typeof element === "object") {
    const firstDetail = Object.values(element).find(
      (v) => typeof v === "string" && v.trim()
    );
    if (firstDetail) return firstDetail as string;
  }
  return (data?.message as string) || fallback;
};

const isOverdue = (lead: Lead): boolean =>
  !!lead.next_action_at &&
  moment(lead.next_action_at).isBefore(moment().startOf("day"));
const isDueToday = (lead: Lead): boolean =>
  !!lead.next_action_at &&
  moment(lead.next_action_at).isSameOrBefore(moment().endOf("day"));
const leadDisplayName = (lead: Lead): string =>
  [lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
  lead.email ||
  lead.phone ||
  "—";

const LeadsPipeline = () => {
  const [t] = useTranslation();
  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const entityId = user.entity_identification;
  const queryClient = useQueryClient();

  const hasLeadsAccess = permissions.hasFeatureAccess("LEADS_MANAGEMENT");

  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [collapsedColumns, setCollapsedColumns] = useState<
    Record<string, boolean>
  >({});
  const toggleColumnCollapse = (colId: string) =>
    setCollapsedColumns((prev) => ({ ...prev, [colId]: !prev[colId] }));
  const [addLeadColumn, setAddLeadColumn] = useState<string | null>(null);
  const [showColumnsConfig, setShowColumnsConfig] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null);
  const [convertEmail, setConvertEmail] = useState("");
  const [convertEmailError, setConvertEmailError] = useState("");
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [leadDetail, setLeadDetail] = useState<Lead | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);

  const { data: summary } = useGetLeadsSummary(
    customer_identification,
    entityId
  );
  const { data: columns = [] } = useGetLeadColumns(
    customer_identification,
    entityId
  );
  const {
    isLoading,
    isError,
    isSuccess,
    data: leads = [],
    refetch,
  } = useGetLeads(customer_identification, entityId);

  const { mutate: updateLead, isLoading: isSavingDetail } = useUpdateLead(
    customer_identification,
    entityId
  );
  const { mutate: convertLead, isLoading: isConverting } = useConvertLead(
    customer_identification,
    entityId
  );
  const { mutate: deleteLead, isLoading: isDeleting } = useDeleteLead(
    customer_identification,
    entityId
  );
  const createLead = useCreateLead(customer_identification, entityId);

  const visibleLeads = useMemo(
    () => (dueTodayOnly ? leads.filter(isDueToday) : leads),
    [leads, dueTodayOnly]
  );

  const canWriteLeads =
    permissions.can(Cap.LEADS, Level.WRITE) && !userEndedSubscription;
  const dragEnabled = useKanbanDndEnabled() && canWriteLeads;

  const leadNameById = useCallback(
    (leadId: string) => {
      const lead = leads.find((item) => item.lead_id === leadId);
      return lead ? leadDisplayName(lead) : "";
    },
    [leads]
  );
  const columnLabelById = useCallback(
    (columnId: string | null) => {
      const column = columns.find((col) => col.id === columnId);
      return column ? columnLabel(column, t) : "";
    },
    [columns, t]
  );

  const dndTexts = useMemo<KanbanDndTexts>(
    () => ({
      instructions: t("leads.dnd.instructions") as string,
      onDragStart: (leadId) =>
        t("leads.dnd.pickedUp", { name: leadNameById(leadId) }) as string,
      onDragOver: (leadId, columnId) =>
        (columnId
          ? t("leads.dnd.over", {
              name: leadNameById(leadId),
              column: columnLabelById(columnId),
            })
          : t("leads.dnd.outside", { name: leadNameById(leadId) })) as string,
      onDragEnd: (leadId, columnId) =>
        (columnId
          ? t("leads.dnd.dropped", {
              name: leadNameById(leadId),
              column: columnLabelById(columnId),
            })
          : t("leads.dnd.cancelled", { name: leadNameById(leadId) })) as string,
      onDragCancel: (leadId) =>
        t("leads.dnd.cancelled", { name: leadNameById(leadId) }) as string,
    }),
    [t, leadNameById, columnLabelById]
  );

  const leadCap = summary?.lead_limit ?? leadLimit(permissions.getPlanTier());
  const leadCount = summary?.lead_count ?? leads.length;
  const atLeadCap = leadCount >= leadCap;
  const openPlans = () => window.dispatchEvent(new Event("openPlansModal"));

  const moveLeadToColumn = useCallback(
    (leadId: string, columnId: string) => {
      if (!canWriteLeads) return;
      if (updatingLeadId === leadId) return;
      const lead = leads.find((item) => item.lead_id === leadId);
      if (!lead) return;
      if (columnId === resolveColumnId(lead.column_id, columns)) return;

      const cacheKey = leadsKey(entityId);
      const previousLeads = queryClient.getQueryData<Lead[]>(cacheKey);
      queryClient.setQueryData<Lead[]>(cacheKey, (current) =>
        (current ?? []).map((item) =>
          item.lead_id === leadId ? { ...item, column_id: columnId } : item
        )
      );

      setUpdatingLeadId(leadId);
      updateLead(
        { leadId, patch: { column_id: columnId } },
        {
          onError: (err) => {
            if (previousLeads) {
              queryClient.setQueryData<Lead[]>(cacheKey, previousLeads);
            } else {
              queryClient.invalidateQueries(cacheKey);
            }
            toast.error(
              (err?.response?.data?.message || t("leads.addError")) as string
            );
          },
          onSettled: () => setUpdatingLeadId(null),
        }
      );
    },
    [
      canWriteLeads,
      columns,
      entityId,
      leads,
      queryClient,
      t,
      updateLead,
      updatingLeadId,
    ]
  );

  const openConvert = (lead: Lead) => {
    setConvertEmail(lead.email || "");
    setConvertEmailError("");
    setLeadToConvert(lead);
  };
  const closeConvert = () => {
    setLeadToConvert(null);
    setConvertEmail("");
    setConvertEmailError("");
  };

  const handleConvert = () => {
    if (!leadToConvert) return;
    const email = convertEmail.trim();
    if (!leadToConvert.email) {
      if (!email) {
        setConvertEmailError(
          t("dashboard.entities.viewEntityUser.emailRequired") as string
        );
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setConvertEmailError(
          t("dashboard.entities.viewEntityUser.invalidEmail") as string
        );
        return;
      }
    }
    setConvertEmailError("");
    convertLead(
      {
        leadId: leadToConvert.lead_id,
        email: leadToConvert.email ? undefined : email,
      },
      {
        onSuccess: () => {
          toast.success(t("leads.convert.success"));
          closeConvert();
        },
        onError: (err) => {
          toast.error(apiErrorMessage(err, t("leads.convert.error") as string));
        },
      }
    );
  };

  const handleDelete = () => {
    if (!leadToDelete) return;
    deleteLead(leadToDelete.lead_id, {
      onSuccess: () => setLeadToDelete(null),
      onError: () => setLeadToDelete(null),
    });
  };

  const handleDetailSave = (patch: UpdateLeadInput, done: () => void) => {
    if (!leadDetail) return;
    updateLead(
      { leadId: leadDetail.lead_id, patch },
      {
        onSuccess: () => {
          setLeadDetail((prev) => {
            if (!prev) return prev;
            const { add_note, edit_note, delete_note, ...fields } = patch;
            let notes = Array.isArray(prev.notes) ? [...prev.notes] : [];
            if (add_note)
              notes.push({ at: new Date().toISOString(), text: add_note });
            if (edit_note && notes[edit_note.index]) {
              notes[edit_note.index] = {
                ...notes[edit_note.index],
                text: edit_note.text,
                edited_at: new Date().toISOString(),
              };
            }
            if (
              delete_note &&
              delete_note.index >= 0 &&
              delete_note.index < notes.length
            ) {
              notes.splice(delete_note.index, 1);
            }
            return { ...prev, ...fields, notes };
          });
          done();
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message || t("leads.addError")) as string
          );
        },
      }
    );
  };

  const columnSelect = (lead: Lead) => (
    <Select
      sizing="sm"
      value={resolveColumnId(lead.column_id, columns)}
      disabled={
        updatingLeadId === lead.lead_id ||
        userEndedSubscription ||
        !permissions.can(Cap.LEADS, Level.WRITE)
      }
      aria-label={
        t("leads.changeStatus", { name: leadDisplayName(lead) }) as string
      }
      onChange={(e) => moveLeadToColumn(lead.lead_id, e.target.value)}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((c) => (
        <option key={c.id} value={c.id}>
          {columnLabel(c, t)}
        </option>
      ))}
    </Select>
  );

  const nextActionBlock = (lead: Lead) => {
    if (!lead.next_action && !lead.next_action_at) return null;
    const overdue = isOverdue(lead);
    return (
      <div
        className={`flex items-start gap-1.5 text-xs ${
          overdue ? "text-red-600 font-medium" : "text-gray-600"
        }`}
      >
        <HiOutlineCalendar
          size={14}
          className={`mt-0.5 shrink-0 ${
            overdue ? "text-red-500" : "text-gray-400"
          }`}
        />
        <span className="min-w-0">
          {lead.next_action && (
            <span className="block truncate" title={lead.next_action}>
              <NoTranslate>{lead.next_action}</NoTranslate>
            </span>
          )}
          {lead.next_action_at && (
            <span className={overdue ? "text-red-600" : "text-gray-500"}>
              {moment(lead.next_action_at).format("ll")}
              {overdue && <> · {t("leads.overdue")}</>}
            </span>
          )}
        </span>
      </div>
    );
  };

  const leadCardClass =
    "group flex cursor-pointer flex-col gap-1.5 rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:border-gray-300";

  const leadCardBody = (lead: Lead) => (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <UserAvatar
            firstName={lead.first_name}
            lastName={lead.last_name}
            email={lead.email}
            sizeClass="w-9 h-9"
            textClass="text-xs"
          />
          <span
            className="text-sm font-semibold text-gray-900 truncate block min-w-0"
            title={leadDisplayName(lead)}
          >
            <NoTranslate>{leadDisplayName(lead)}</NoTranslate>
          </span>
        </div>
        {permissions.can(Cap.LEADS, Level.DELETE) && (
          <span data-dnd-ignore className="shrink-0">
            <CustomButton
              type="button"
              variant="none"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setLeadToDelete(lead);
              }}
              disabled={isDeleting}
              className="rounded-md text-gray-400 transition-opacity enabled:hover:text-red-600 md:opacity-0 md:group-hover:opacity-100"
              icon={<HiOutlineTrash size={18} />}
              aria-label={t("leads.delete") as string}
            />
          </span>
        )}
      </div>

      {nextActionBlock(lead)}

      {!dragEnabled && (
        <div
          data-dnd-ignore
          className="mt-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {columnSelect(lead)}
        </div>
      )}
    </>
  );

  const leadCard = (lead: Lead, columnId: string) => (
    <KanbanCard
      key={lead.lead_id}
      cardId={lead.lead_id}
      columnId={columnId}
      disabled={updatingLeadId === lead.lead_id}
      className={leadCardClass}
      ariaLabel={
        t("leads.dnd.cardLabel", {
          name: leadDisplayName(lead),
          column: columnLabelById(columnId),
        }) as string
      }
      onActivate={() => setLeadDetail(lead)}
    >
      {leadCardBody(lead)}
    </KanbanCard>
  );

  /** The card that follows the pointer: same skin, no draggable of its own. */
  const leadDragPreview = (leadId: string) => {
    const lead = leads.find((item) => item.lead_id === leadId);
    if (!lead) return null;
    return (
      <div className={twMerge(leadCardClass, "cursor-grabbing shadow-lg")}>
        {leadCardBody(lead)}
      </div>
    );
  };

  const gridColsClass =
    [
      "",
      "xl:grid-cols-1",
      "xl:grid-cols-2",
      "xl:grid-cols-3",
      "xl:grid-cols-4",
      "xl:grid-cols-5",
    ][Math.min(columns.length, 5)] || "xl:grid-cols-5";

  const kanbanView = (
    <KanbanDnd
      disabled={!canWriteLeads}
      onMoveCard={moveLeadToColumn}
      renderDragOverlay={leadDragPreview}
      texts={dndTexts}
    >
      <div className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-4`}>
        {columns.map((col) => {
          const columnLeads = visibleLeads.filter(
            (lead) => resolveColumnId(lead.column_id, columns) === col.id
          );
          const collapsed = collapsedColumns[col.id];
          return (
            <KanbanColumn
              key={col.id}
              columnId={col.id}
              className="bg-gray-50 rounded-lg p-3 md:min-h-[200px]"
            >
              <button
                type="button"
                onClick={() => toggleColumnCollapse(col.id)}
                className="w-full flex items-center justify-between mb-3 md:cursor-default"
                aria-expanded={!collapsed}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <HiChevronDown
                    size={16}
                    className={`text-gray-400 shrink-0 md:hidden transition-transform ${
                      collapsed ? "-rotate-90" : ""
                    }`}
                  />
                  <span className="truncate text-sm font-semibold text-gray-900">
                    {columnLabel(col, t)}
                  </span>
                </span>
                <span className="shrink-0 text-xs font-medium tabular-nums text-gray-500">
                  <NoTranslate>{columnLeads.length}</NoTranslate>
                </span>
              </button>
              <div
                className={`flex-col gap-2 ${
                  collapsed ? "hidden md:flex" : "flex"
                }`}
              >
                {columnLeads.map((lead) => leadCard(lead, col.id))}
                {atLeadCap ? (
                  <button
                    type="button"
                    onClick={openPlans}
                    className="w-full flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-amber-50 transition-colors"
                    title={
                      t("leads.leadLimitReached", { limit: leadCap }) as string
                    }
                  >
                    <UnlockBadge onClick={openPlans} />
                  </button>
                ) : (
                  permissions.can(Cap.LEADS, Level.WRITE) && (
                    <CustomButton
                      type="button"
                      variant="none"
                      size="sm"
                      onClick={() => setAddLeadColumn(col.id)}
                      disabled={userEndedSubscription}
                      className="w-full justify-start gap-1.5 rounded-md px-2 py-1.5 text-gray-500 enabled:hover:bg-gray-100 enabled:hover:text-gray-700"
                      icon={<HiOutlinePlus size={16} />}
                      title={t("leads.newLead")}
                    />
                  )
                )}
              </div>
            </KanbanColumn>
          );
        })}
      </div>
    </KanbanDnd>
  );

  const emptyState = (
    <>
      <LeadsGettingStarted />
      {permissions.can(Cap.LEADS, Level.WRITE) && (
        <div className="flex justify-center mt-6">
          <CustomButton
            variant="primary"
            icon="plus"
            disabled={userEndedSubscription}
            title={t("leads.addLead") as string}
            onClick={() => setAddLeadColumn("")}
          />
        </div>
      )}
    </>
  );

  if (!hasLeadsAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-gray-50">
            <HiOutlineLockClosed className="h-5 w-5 text-gray-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("leads.lock.title")}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("leads.lock.description")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-3">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("leads.pageTitle")}
          </h1>
          {summary?.lead_limit != null && (
            <p className="mt-0.5 text-sm text-gray-500">
              {t("leads.summary.leadsChipCapped", {
                current: summary?.lead_count ?? 0,
                limit: summary?.lead_limit,
              })}
              {!atLeadCap && summary.lead_limit < 100 && (
                <> · {t("leads.summary.upsellFromPremium")}</>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {atLeadCap && <UnlockBadge onClick={openPlans} />}
          <CustomButton
            variant={dueTodayOnly ? "custom" : "secondary"}
            size="sm"
            className={
              dueTodayOnly
                ? "border border-red-300 bg-red-50 text-red-700 enabled:hover:bg-red-100"
                : undefined
            }
            aria-pressed={dueTodayOnly}
            onClick={() => setDueTodayOnly((prev) => !prev)}
            comp={
              <span className="flex items-center gap-1.5">
                <HiOutlineCalendar size={16} className="shrink-0" />
                {t("leads.filterDueToday")}
              </span>
            }
          />

          {permissions.can(Cap.LEADS, Level.WRITE) && (
            <div className="flex items-center gap-1">
              <ToolbarDivider />
              <ToolbarButton
                aria-label={t("leads.columns.configure") as string}
                onClick={() => setShowColumnsConfig(true)}
                icon={<HiOutlineAdjustments size={16} className="shrink-0" />}
                title={t("leads.columns.configure")}
              />
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="min-h-[340px] flex items-center justify-center">
          <AiOutlineLoading className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      )}

      {isError && (
        <div className="min-h-[340px] flex items-center justify-center">
          <CustomButton
            variant="primary"
            onClick={() => refetch()}
            title={t("leads.reload") as string}
          />
        </div>
      )}

      {isSuccess && leads.length === 0 && emptyState}

      {isSuccess && leads.length > 0 && (
        <>
          <CollapsibleHowItWorks
            collapsible
            title={t("leads.howItWorksToggle")}
            icon={<HiOutlineFilter className="text-blue-600" size={18} />}
            className="mb-4"
          >
            <LeadsGettingStarted />
          </CollapsibleHowItWorks>

          {dueTodayOnly && visibleLeads.length === 0 ? (
            <EmptyState
              icon={<HiOutlineCalendar size={24} />}
              title={t("leads.noDueToday")}
            />
          ) : (
            kanbanView
          )}
        </>
      )}

      <AddLeadModal
        show={addLeadColumn !== null}
        onClose={() => setAddLeadColumn(null)}
        isSubmitting={createLead.isLoading}
        onSubmit={(input) =>
          createLead.mutate(
            { ...input, column_id: addLeadColumn || undefined },
            {
              onSuccess: () => setAddLeadColumn(null),
              onError: (err) => {
                toast.error(
                  (err?.response?.data?.message ||
                    t("leads.addError")) as string
                );
              },
            }
          )
        }
      />

      <ColumnsConfigModal
        show={showColumnsConfig}
        onClose={() => setShowColumnsConfig(false)}
        columns={columns}
      />

      <LeadDetailModal
        lead={leadDetail}
        columns={columns}
        isSaving={isSavingDetail}
        onClose={() => setLeadDetail(null)}
        onSave={handleDetailSave}
        onConvert={(lead) => {
          setLeadDetail(null);
          openConvert(lead);
        }}
      />

      <ModalShell
        show={!!leadToConvert}
        onClose={closeConvert}
        position="center"
        dismissible
        footer={
          <>
            <CustomButton
              onClick={closeConvert}
              title={t("common.cancel") as string}
              variant="secondary"
            />
            {permissions.can(Cap.LEADS, Level.WRITE) &&
              (leadToConvert && !leadToConvert.email ? (
                <CustomButton
                  type="submit"
                  form="convert-lead-form"
                  variant="primary"
                  disabled={isConverting}
                  isLoading={isConverting}
                  title={t("leads.convert.confirm") as string}
                />
              ) : (
                <CustomButton
                  onClick={handleConvert}
                  variant="primary"
                  disabled={isConverting}
                  isLoading={isConverting}
                  title={t("leads.convert.confirm") as string}
                />
              ))}
          </>
        }
      >
        <div className="grid grid-rows-[auto_1fr] gap-4 justify-items-center text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full grid place-items-center">
            <HiSwitchHorizontal size={24} className="text-green-600" />
          </div>
          <div className="space-y-2">
            <div className="text-gray-900 text-lg font-semibold font-figtree leading-7 break-words">
              {t("leads.convert.title")}
            </div>
            <div className="text-gray-500 text-sm font-normal font-figtree leading-tight break-words">
              {t("leads.convert.message", {
                name: leadToConvert ? leadDisplayName(leadToConvert) : "",
              })}
            </div>
          </div>
        </div>

        {leadToConvert && !leadToConvert.email && (
          <form
            id="convert-lead-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleConvert();
            }}
            className="mt-4"
          >
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              {t("leads.convert.emailLabel")}
            </label>
            <TextInput
              type="email"
              required
              color={convertEmailError ? "failure" : undefined}
              value={convertEmail}
              autoFocus
              placeholder={t("leads.convert.emailPlaceholder") as string}
              onChange={(e) => {
                setConvertEmail(e.target.value);
                if (convertEmailError) setConvertEmailError("");
              }}
            />
            {convertEmailError ? (
              <p className="mt-1 text-left text-xs text-red-600" role="alert">
                {convertEmailError}
              </p>
            ) : (
              <p className="text-xs text-gray-400 mt-1 text-left">
                {t("leads.convert.emailHint")}
              </p>
            )}
          </form>
        )}
      </ModalShell>

      <ConfirmModal
        show={!!leadToDelete && permissions.can(Cap.LEADS, Level.DELETE)}
        onClose={() => setLeadToDelete(null)}
        title={t("leads.delete") as string}
        description={
          t("leads.deleteConfirm", {
            name: leadToDelete ? leadDisplayName(leadToDelete) : "",
          }) as string
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setLeadToDelete(null)}
        confirmBtnName={t("leads.delete") as string}
        confirmBtnFn={handleDelete}
        isLoading={isDeleting}
        ConfirmBtnColor="red"
      />
    </div>
  );
};

export default LeadsPipeline;
